import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '..';
import type { Injections } from '../services/injections';
import type { PackagingSetV2, PartnerPackagingV2 } from '../services/PartnerService/types';
import type { ApplicationErrorOptions } from '../../types';
import parseStatusCode from '../errors/parseStatusCode';

export interface PackagingModelV2 {
  // #region Data
  activePartnerPackagingV2?: PartnerPackagingV2;
  partnerPackagingV2?: PartnerPackagingV2[];
  partnerPackagingSets?: PackagingSetV2[];
  lastAction?: number;
  // #endregion

  // #region Create Packaging Action
  packagingActionStart: Action<PackagingModelV2, void>;
  packagingConfigFailure: Action<PackagingModelV2, ApplicationErrorOptions>;
  // #endregion

  // #region Partner Packaging
  createPartnerPackagingV2: Thunk<
    PackagingModelV2,
    PartnerPackagingV2,
    Injections,
    StoreModel,
    Promise<PartnerPackagingV2 | Error>
  >;
  updatePartnerPackagingV2: Thunk<
    PackagingModelV2,
    PartnerPackagingV2,
    Injections,
    StoreModel,
    Promise<PartnerPackagingV2 | Error>
  >;
  fetchPartnerPackagingV2: Thunk<
    PackagingModelV2,
    void,
    Injections,
    StoreModel,
    Promise<PartnerPackagingV2[] | undefined>
  >;
  setPartnerPackagingCollectionV2: Action<PackagingModelV2, PartnerPackagingV2[]>;
  // #endregion

  // #region Packaging Sets
  createPackagingSet: Thunk<PackagingModelV2, PackagingSetV2, Injections, StoreModel, Promise<PackagingSetV2 | Error>>;
  fetchPackagingSets: Thunk<PackagingModelV2, void, Injections, StoreModel, Promise<PackagingSetV2[] | undefined>>;
  setPartnerPackagingSets: Action<PackagingModelV2, PackagingSetV2[]>;
  updatePackagingSet: Thunk<PackagingModelV2, any, Injections, StoreModel, Promise<PackagingSetV2 | Error>>;
  addPackagingToSet: Thunk<PackagingModelV2, any, Injections, StoreModel, Promise<PackagingSetV2 | Error>>;
  deletePackagingFromSet: Thunk<PackagingModelV2, any, Injections, StoreModel, Promise<PackagingSetV2 | Error>>;
  // #endregion
}

export const packagingModelV2: PackagingModelV2 = {
  packagingConfigFailure: action((state, error) => {
    console.error(error);
  }),
  packagingActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  fetchPartnerPackagingV2: thunk(async (actions, payload, { injections }) => {
    try {
      actions.packagingActionStart();
      const { partnerService } = injections;
      const response = await partnerService.fetchPartnerPackagingV2();
      actions.setPartnerPackagingCollectionV2(response);
      return response;
    } catch (error) {
      actions.packagingConfigFailure({
        error,
        message: 'An error occured fetching partner packaging.',
        asNotification: false,
        httpCode: parseStatusCode(error),
        duration: 0,
      });
    }
  }),
  setPartnerPackagingCollectionV2: action((state, payload) => {
    state.partnerPackagingV2 = payload;
  }),
  setPartnerPackagingSets: action((state, payload) => {
    state.partnerPackagingSets = payload;
  }),
  createPartnerPackagingV2: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.packagingActionStart();
      const { partnerService } = injections;
      const response = await partnerService.createPartnerPackagingV2(payload);
      actions.fetchPartnerPackagingV2();
      return response;
    } catch (error) {
      actions.packagingConfigFailure({
        error,
        message: 'An error creating partner packaging.',
        asNotification: false,
        httpCode: parseStatusCode(error),
        duration: 0,
      });
      return error;
    }
  }),
  updatePartnerPackagingV2: thunk(async (actions, payload, { injections }) => {
    try {
      actions.packagingActionStart();
      const { partnerService } = injections;
      const response = await partnerService.updatePartnerPackagingV2(payload);
      actions.fetchPartnerPackagingV2();
      return response;
    } catch (error) {
      actions.packagingConfigFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error updating partner packaging.',
        asNotification: true,
        duration: 0,
      });
      return error;
    }
  }),
  createPackagingSet: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.packagingActionStart();
      const { partnerService } = injections;
      const response = await partnerService.createPackagingSet(payload);
      actions.fetchPackagingSets();
      return response;
    } catch (error) {
      actions.packagingConfigFailure({
        error,
        message: 'An error creating partner packaging.',
        asNotification: false,
        httpCode: parseStatusCode(error),
        duration: 0,
      });
      return error;
    }
  }),
  fetchPackagingSets: thunk(async (actions, payload, { injections }) => {
    try {
      actions.packagingActionStart();
      const { partnerService } = injections;
      const response = await partnerService.fetchPackagingSets();
      actions.setPartnerPackagingSets(response);
      return response;
    } catch (error) {
      actions.packagingConfigFailure({
        error,
        message: 'An error occured fetching partner packaging.',
        asNotification: false,
        httpCode: parseStatusCode(error),
        duration: 0,
      });
    }
  }),
  addPackagingToSet: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.packagingActionStart();
      const { partnerService } = injections;
      const response = await partnerService.addPackagingToSet(payload.packagingSetId, payload.packagingId);
      actions.fetchPackagingSets();
      return response;
    } catch (error) {
      actions.packagingConfigFailure({
        error,
        message: 'An error adding packaging to packaging set.',
        asNotification: false,
        httpCode: parseStatusCode(error),
        duration: 0,
      });
      return error;
    }
  }),
  deletePackagingFromSet: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.packagingActionStart();
      const { partnerService } = injections;
      const response = await partnerService.deletePackagingFromSet(payload.packagingSetId, payload.packagingId);
      actions.fetchPackagingSets();
      return response;
    } catch (error) {
      actions.packagingConfigFailure({
        error,
        message: 'An error deleting packaging from packaging set.',
        asNotification: false,
        httpCode: parseStatusCode(error),
        duration: 0,
      });
      return error;
    }
  }),
  updatePackagingSet: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.packagingActionStart();
      const { partnerService } = injections;
      const response = await partnerService.updatePackagingSet(payload.packagingSetId, { ...payload });
      actions.fetchPackagingSets();
      return response;
    } catch (error) {
      actions.packagingConfigFailure({
        error,
        message: 'An error updating partner packaging.',
        asNotification: false,
        httpCode: parseStatusCode(error),
        duration: 0,
      });
      return error;
    }
  }),
};
